import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const { logoSrc } = useBranding();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex bg-foreground text-background p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] bg-grid-dense pointer-events-none" />
        <div className="relative flex items-center gap-3">
          {logoSrc ? (
            <img src={logoSrc} alt="" className="h-12 object-contain bg-background p-1.5" />
          ) : (
            <div className="h-7 w-7 bg-background" />
          )}
          <span className="font-mono text-sm tracking-tight">EARLY YEARS ACADEMY</span>
        </div>
        <div className="relative">
          <div className="font-mono text-[10px] tracking-widest text-background/60 mb-6">/ ADMIN ENTRY</div>
          <h2 className="text-5xl font-black tracking-tighter leading-[0.95]">
            Build courses.<br />Send links.<br />Issue certificates.
          </h2>
          <p className="mt-6 max-w-md text-sm text-background/70 leading-relaxed">
            A single workspace for digital CPD training providers. Designed for clarity.
          </p>
        </div>
        <div className="relative font-mono text-[10px] tracking-widest text-background/40">
          © 2026 — A FOCUSED LMS
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col">
        <div className="p-6 md:p-12">
          <Link to="/" data-testid="back-to-home" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={14} strokeWidth={1.5} /> BACK
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 md:px-12 pb-12">
          <form onSubmit={onSubmit} className="w-full max-w-sm space-y-8">
            <div>
              <div className="eyebrow mb-3">/ Sign in</div>
              <h1 className="text-4xl font-black tracking-tighter">Admin login</h1>
              <p className="mt-3 text-sm text-muted-foreground">Use your admin credentials to access the workspace.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold tracking-[0.2em] uppercase">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-sm h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold tracking-[0.2em] uppercase">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-sm h-11"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-foreground text-background hover:bg-foreground/90"
            >
              {busy ? "Signing in..." : (<span className="inline-flex items-center gap-2">Sign in <ArrowRight size={16} strokeWidth={1.5} /></span>)}
            </Button>

            <p className="text-xs font-mono text-muted-foreground text-center">
              Use the admin credentials provided to you.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
